import { create } from "zustand"
import { asRecord } from "~/utils/helpers"

interface IRfpOsQnaStore {
  answersRephrasing: Record<string, boolean>
  setAnswersRephrasing: (id: string, value: boolean) => void
  qnaResponses: Record<string, any>
  setQnaResponses: (id: string, value: any) => void
  setConfidenceScore: (id: string, value: any) => void
  subSessionId: string
  setSubSessionId: (id: string) => void
  currentSubSessionId: string
  setCurrentSubSessionId: (id: string) => void
  sourcesDrawerData: any
  setSourcesDrawerData: (value: any) => void
  showAnimation: boolean
  setShowAnimation: (value: boolean) => void
  showAnimationBySubSession: Record<string, boolean>
  setShowAnimationForSubSession: (subSessionId: string, value: boolean) => void
  attachedFiles: { name: string; fileId?: string }[]
  setAttachedFiles: (value: any) => void
  isLoading: boolean
  setIsLoading: (value: boolean) => void
  isLoadingBySubSession: Record<string, boolean>
  setIsLoadingForSubSession: (subSessionId: string, value: boolean) => void
  messagesBySubSession: Record<string, any[]>
  pendingBySubSession: Record<string, boolean>
  setPendingForSubSession: (subSessionId: string, pending: boolean) => void
  historyInProgressBySubSession: Record<string, boolean>
  setHistoryInProgressForSubSession: (
    subSessionId: string,
    inProgress: boolean
  ) => void
  appendMessageForSubSession: (subSessionId: string, message: any) => void
  upsertMessageForSubSession: (
    subSessionId: string,
    message: any,
    matchBy?: (a: any, b: any) => boolean
  ) => void
  setMessagesForSubSession: (subSessionId: string, messages: any[]) => void
  clearMessagesForSubSession: (subSessionId: string) => void
  cleanup: () => void
}

const useRfpOsQnaStore = create<IRfpOsQnaStore>((set) => ({
  answersRephrasing: {},
  setAnswersRephrasing: (id, value) =>
    set((state) => ({
      answersRephrasing: { ...asRecord(state.answersRephrasing), [id]: value }
    })),
  qnaResponses: {},
  setQnaResponses: (id, value) => {
    if (id === "PENDING_REQUESTS_ADD") {
      set((state) => ({
        qnaResponses: {
          ...asRecord(state.qnaResponses),
          PENDING_REQUESTS: (state.qnaResponses.PENDING_REQUESTS ?? []).concat(
            value
          )
        }
      }))
    } else if (id === "PENDING_REQUESTS_REMOVE") {
      set((state) => ({
        qnaResponses: {
          ...asRecord(state.qnaResponses),
          PENDING_REQUESTS: (state.qnaResponses.PENDING_REQUESTS ?? []).filter(
            (item: string) => item !== value
          )
        }
      }))
    } else if (id === "RESET_QNA_RESPONSES") {
      set(() => ({
        qnaResponses: {}
      }))
    } else {
      set((state) => ({
        qnaResponses: { ...asRecord(state.qnaResponses), [id]: value }
      }))
    }
  },
  setConfidenceScore: (id, value) =>
    set((state) => ({
      qnaResponses: {
        ...asRecord(state.qnaResponses),
        [id]: {
          ...state.qnaResponses?.[id],
          score: value.score,
          score_category: value.score_category
        }
      }
    })),
  subSessionId: "",
  setSubSessionId: (id) => set({ subSessionId: id }),
  currentSubSessionId: "",
  setCurrentSubSessionId: (id) => set({ currentSubSessionId: id }),
  sourcesDrawerData: null,
  setSourcesDrawerData: (value) => set({ sourcesDrawerData: value }),
  showAnimation: false,
  setShowAnimation: (value) => set({ showAnimation: value }),
  showAnimationBySubSession: {},
  setShowAnimationForSubSession: (subSessionId, value) =>
    set((state) => ({
      showAnimationBySubSession: {
        ...asRecord(state.showAnimationBySubSession),
        [subSessionId]: value
      }
    })),
  attachedFiles: [],
  setAttachedFiles: (value) => set({ attachedFiles: value }),
  isLoading: false,
  setIsLoading: (value) => set({ isLoading: value }),
  isLoadingBySubSession: {},
  setIsLoadingForSubSession: (subSessionId, value) =>
    set((state) => ({
      isLoadingBySubSession: {
        ...asRecord(state.isLoadingBySubSession),
        [subSessionId]: value
      }
    })),
  messagesBySubSession: {},
  pendingBySubSession: {},
  setPendingForSubSession: (subSessionId, pending) =>
    set((state) => ({
      pendingBySubSession: {
        ...asRecord(state.pendingBySubSession),
        [subSessionId]: pending
      }
    })),
  historyInProgressBySubSession: {},
  setHistoryInProgressForSubSession: (subSessionId, inProgress) =>
    set((state) => ({
      historyInProgressBySubSession: {
        ...asRecord(state.historyInProgressBySubSession),
        [subSessionId]: inProgress
      }
    })),
  appendMessageForSubSession: (subSessionId, message) =>
    set((state) => {
      const existing = state.messagesBySubSession?.[subSessionId] ?? []
      return {
        messagesBySubSession: {
          ...asRecord(state.messagesBySubSession),
          [subSessionId]: [...existing, message]
        }
      }
    }),
  upsertMessageForSubSession: (subSessionId, message, matchBy) =>
    set((state) => {
      const existing = state.messagesBySubSession?.[subSessionId] ?? []
      const matcher =
        matchBy ||
        ((a: any, b: any) => {
          return a?.timestamp === b?.timestamp
        })
      const index = existing.findIndex((m: any) => matcher(m, message))
      let updated: any[]
      if (index !== -1) {
        updated = [...existing]
        updated[index] = { ...updated[index], ...message }
      } else {
        updated = [...existing, message]
      }
      return {
        messagesBySubSession: {
          ...asRecord(state.messagesBySubSession),
          [subSessionId]: updated
        }
      }
    }),
  setMessagesForSubSession: (subSessionId, messages) =>
    set((state) => ({
      messagesBySubSession: {
        ...asRecord(state.messagesBySubSession),
        [subSessionId]: messages
      }
    })),
  clearMessagesForSubSession: (subSessionId) =>
    set((state) => {
      const clone = { ...asRecord(state.messagesBySubSession) }
      // @ts-expect-error element implicitly any from string index
      delete clone[subSessionId]
      return { messagesBySubSession: clone }
    }),
  cleanup: () =>
    set({
      qnaResponses: {},
      answersRephrasing: {},
      showAnimationBySubSession: {},
      isLoadingBySubSession: {},
      pendingBySubSession: {},
      historyInProgressBySubSession: {},
      messagesBySubSession: {}
    })
}))

export default useRfpOsQnaStore
